import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthIsAuthenticated } from '@/core/auth/authHooks';
import { getAccessTokenSilent } from '@/core/auth/tokenProvider';

interface RequireAuthProps {
  readonly children: ReactNode;
}

/**
 * Route guard for the native-auth session (US-E10-01). Replaces the former MSAL
 * `useMsal`/`InteractionStatus`/`loginRedirect` guard and the Demo-Login bypass, both fully
 * removed — auth state now comes solely from the app-token store
 * (`useAuthIsAuthenticated` -> `usePrincipal() !== null`).
 *
 * On first mount without a resolved session, **always** attempts a cookie-based refresh
 * (`tokenProvider.getAccessTokenSilent` -> `appToken.getAppAccessToken` -> body-less
 * `POST /auth/refresh`) before deciding — regardless of any `localStorage` state. The browser
 * attaches the HttpOnly refresh cookie automatically if a session/persistent cookie survived the
 * reload/tab-close, so a returning user's session is restored without a visible bounce through
 * `/login`. A 401 (no/expired/invalid cookie) or a 403 (e.g. `NO_ACTIVE_MEMBERSHIP` — the
 * membership backing the session was revoked) both resolve to "no token", which this guard treats
 * identically as a clean session end. Anyone still unauthenticated after that attempt — including
 * a user who just logged out at runtime — is redirected to the public `/login` route via
 * `<Navigate>`, never an outbound Entra redirect (AK7 guardrail).
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useAuthIsAuthenticated();
  const [isRestoring, setIsRestoring] = useState(!isAuthenticated);
  // Guards against a second boot-refresh attempt: React 19 StrictMode double-invokes effects, and
  // a later, unrelated re-render (e.g. runtime logout) must NOT re-trigger it.
  const restoreAttempted = useRef(false);

  useEffect(() => {
    if (isAuthenticated || restoreAttempted.current) return;
    restoreAttempted.current = true;
    void getAccessTokenSilent().finally(() => setIsRestoring(false));
  }, [isAuthenticated]);

  if (isAuthenticated) {
    return <>{children}</>;
  }

  if (isRestoring) {
    return <AuthStatus message="Anmelden…" />;
  }

  return <Navigate to="/login" replace />;
}

function AuthStatus({ message }: { readonly message: string }) {
  return (
    <div className="flex h-screen items-center justify-center" role="status" aria-live="polite">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}
